import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@safe-minds-ai/ui';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  ShieldCheck,
  ShieldAlert,
  Users,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboardBreakdowns, getDashboardOverview, getDashboardTrends } from '../api';

type TrendPoint = { day: string; c: number };
type Series = { key: string; label: string; points: TrendPoint[]; previousPoints?: TrendPoint[] };

export default function DashboardRoute(): JSX.Element {
  const navigate = useNavigate();
  const [rangePreset, setRangePreset] = useState<'7d' | '30d' | '90d'>('30d');
  const [compareMode, setCompareMode] = useState<'previous_period' | 'previous_week' | 'previous_month' | 'same_period_last_month'>('previous_period');

  const range = useMemo(() => {
    const to = new Date();
    const days = rangePreset === '7d' ? 7 : rangePreset === '90d' ? 90 : 30;
    const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [rangePreset]);

  const { data: overview, isLoading: loadingOverview, error: overviewError } = useQuery({
    queryKey: ['admin-dashboard-overview', range, compareMode],
    queryFn: () => getDashboardOverview({ ...range, compareMode }),
    staleTime: 30_000,
  });

  const { data: trends, isLoading: loadingTrends } = useQuery({
    queryKey: ['admin-dashboard-trends', range, compareMode],
    queryFn: () => getDashboardTrends({ ...range, compareMode, granularity: 'day' }),
    staleTime: 30_000,
  });

  const { data: severityBreakdown } = useQuery({
    queryKey: ['admin-dashboard-breakdown', range, compareMode, 'severity'],
    queryFn: () => getDashboardBreakdowns({ ...range, compareMode, dimension: 'severity' }),
    staleTime: 30_000,
  });

  const { data: topicBreakdown } = useQuery({
    queryKey: ['admin-dashboard-breakdown', range, compareMode, 'topic'],
    queryFn: () => getDashboardBreakdowns({ ...range, compareMode, dimension: 'topic' }),
    staleTime: 30_000,
  });

  const kpisByKey = useMemo(() => {
    const map = new Map<string, (typeof overview)['kpis'][number]>();
    overview?.kpis?.forEach((kpi) => map.set(kpi.key, kpi));
    return map;
  }, [overview]);

  const kpiCards = [
    { key: 'activeParents', label: 'Active Parents', icon: <Users className="h-4 w-4 text-[#3B5BD6]" /> },
    { key: 'activeChildren', label: 'Active Children', icon: <Users className="h-4 w-4 text-[#3B5BD6]" /> },
    { key: 'totalSessions', label: 'Total Sessions', icon: <Activity className="h-4 w-4 text-[#3B5BD6]" /> },
    { key: 'safetyScore', label: 'Safety Score', icon: <ShieldCheck className="h-4 w-4 text-emerald-500" /> },
    { key: 'violationsRate', label: 'Violations Rate', icon: <ShieldAlert className="h-4 w-4 text-rose-500" /> },
    { key: 'retentionRate', label: 'Retention', icon: <Activity className="h-4 w-4 text-[#3B5BD6]" /> },
  ];

  return (
    <div className="space-y-8 text-slate-700 dark:text-slate-100">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
            Welcome back, Admin Control 👋
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-300">
            Monitor usage, flags, and moderation health at a glance.
          </p>
        </div>
        <Button
          variant="outline"
          className="rounded-full border-slate-200/80 bg-white/80 px-4 text-slate-600 shadow-sm dark:border-slate-600 dark:bg-slate-800/80 dark:text-slate-100 dark:hover:bg-slate-700/80"
        >
          Admin Control
        </Button>
      </div>

      <Card className="rounded-3xl border border-slate-200/40 bg-white/80 p-5 shadow-[0_20px_50px_-40px_rgba(30,64,175,0.35)] dark:border-slate-700/40 dark:bg-slate-900/80">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="space-y-1">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400 dark:text-slate-300">Executive Summary</p>
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-300">
              <span>Last updated overview</span>
              <span
                className="rounded-full border border-slate-200/70 bg-white px-2 py-0.5 text-[11px] text-slate-500 shadow-sm dark:border-slate-700/50 dark:bg-slate-800/80 dark:text-slate-300"
                title={overview?.comparisonLabel ?? ''}
              >
                vs previous period
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={rangePreset} onValueChange={(value) => setRangePreset(value as typeof rangePreset)}>
              <SelectTrigger className="h-9 min-w-[150px] rounded-full bg-white/90 text-sm dark:bg-slate-800/80">
                <SelectValue placeholder="Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="90d">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Select value={compareMode} onValueChange={(value) => setCompareMode(value as typeof compareMode)}>
              <SelectTrigger className="h-9 min-w-[170px] rounded-full bg-white/90 text-sm dark:bg-slate-800/80">
                <SelectValue placeholder="Compare" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="previous_period">Previous period</SelectItem>
                <SelectItem value="previous_week">Previous week</SelectItem>
                <SelectItem value="previous_month">Previous month</SelectItem>
                <SelectItem value="same_period_last_month">Same period last month</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {kpiCards.map((card) => {
          const kpi = card.key === 'safetyScore' ? null : kpisByKey.get(card.key);
          const value = card.key === 'safetyScore'
            ? overview?.safetyScore?.score ?? 0
            : kpi?.currentValue ?? 0;
          const delta = card.key === 'safetyScore'
            ? overview?.safetyScore?.deltaPercent ?? null
            : kpi?.deltaPercent ?? null;
          const isPercent = card.key !== 'safetyScore' && kpi?.isPercent;
          const comparisonLabel = card.key === 'safetyScore'
            ? overview?.comparisonLabel ?? ''
            : kpi?.comparisonLabel ?? '';
          return (
            <Card
              key={card.key}
              className="rounded-3xl border border-slate-200/40 bg-white/80 shadow-[0_20px_50px_-40px_rgba(30,64,175,0.35)] transition hover:-translate-y-0.5 dark:border-slate-700/40 dark:bg-slate-900/80"
              role="button"
              onClick={() => {
                if (card.key === 'activeParents' || card.key === 'activeChildren') {
                  navigate('/users');
                  return;
                }
                if (card.key === 'violationsRate') {
                  navigate('/flags');
                  return;
                }
              }}
            >
              <CardHeader className="gap-2 pb-2">
                <CardDescription className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-300">
                  {card.icon}
                  {card.label}
                </CardDescription>
                <CardTitle className="flex items-end gap-2 text-2xl text-slate-900 dark:text-slate-100">
                  {isPercent ? `${(value * 100).toFixed(0)}%` : Math.round(value).toLocaleString()}
                  {delta !== null ? (
                    <span
                      className={
                        delta >= 0
                          ? 'rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200'
                          : 'rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700 dark:bg-rose-500/20 dark:text-rose-200'
                      }
                      title={`Compared to: ${comparisonLabel}`}
                    >
                      {delta >= 0 ? '+' : ''}
                      {delta.toFixed(1)}%
                    </span>
                  ) : null}
                </CardTitle>
                <p className="text-xs text-slate-400 dark:text-slate-400">
                  <span title={comparisonLabel}>vs previous period</span>
                </p>
              </CardHeader>
            </Card>
          );
        })}
      </div>

      <div className="grid gap-6 xl:grid-cols-[2.2fr_1fr]">
        <div className="space-y-4">
          <Card
            className="rounded-3xl border border-slate-200/40 bg-white/80 shadow-[0_24px_60px_-45px_rgba(30,64,175,0.35)] dark:border-slate-700/40 dark:bg-slate-900/80"
          >
            <CardHeader className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle className="text-lg text-slate-900 dark:text-slate-100">Growth & Engagement</CardTitle>
                <CardDescription className="dark:text-slate-300">Active usage and messaging trends.</CardDescription>
              </div>
              <span
                className="inline-flex items-center gap-2 rounded-full border border-slate-200/70 bg-white/80 px-3 py-1 text-[11px] text-slate-500 shadow-sm dark:border-slate-700/50 dark:bg-slate-800/80 dark:text-slate-300"
                title={overview?.comparisonLabel ?? ''}
              >
                vs previous period
              </span>
            </CardHeader>
            <CardContent className="space-y-4">
              <MultiLineChart
                series={trends?.series ?? []}
                isLoading={loadingTrends}
              />
              <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200/50 bg-white/70 px-3 py-2 text-sm text-slate-600 dark:border-slate-700/40 dark:bg-slate-900/80 dark:text-slate-200">
                {(trends?.series ?? []).map((series) => (
                  <span key={series.key} className="flex items-center gap-2 rounded-full bg-white px-3 py-1 shadow-sm dark:bg-slate-800/80">
                    <span className="text-xs text-slate-500 dark:text-slate-300">{series.label}</span>
                    <strong className="text-slate-900 dark:text-slate-100">
                      {series.points.reduce((acc, point) => acc + point.c, 0).toLocaleString()}
                    </strong>
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card
            className="rounded-3xl border border-slate-200/40 bg-white/80 shadow-[0_24px_60px_-45px_rgba(30,64,175,0.35)] dark:border-slate-700/40 dark:bg-slate-900/80"
          >
            <CardHeader>
              <CardTitle className="text-lg text-slate-900 dark:text-slate-100">Safety & Compliance</CardTitle>
              <CardDescription className="dark:text-slate-300">How well the system is keeping learners safe.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-6 lg:grid-cols-[1.15fr_1fr]">
              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-200/50 bg-white/90 p-4 dark:border-slate-700/40 dark:bg-slate-900/80">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-300">Safety Score</p>
                      <p className="text-3xl font-semibold text-slate-900 dark:text-slate-100">
                        {overview?.safetyScore?.score?.toFixed(0) ?? '—'}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-300">
                        {overview?.safetyScore?.topDrivers?.improving ?? 'Tracking safety performance.'}
                      </p>
                    </div>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="rounded-full text-xs">What is this?</Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>Safety Score</DialogTitle>
                        </DialogHeader>
                        <p className="text-sm text-slate-600 dark:text-slate-300">
                          {overview?.safetyScore?.definition}
                        </p>
                        <div className="mt-4 grid gap-3">
                          {overview?.safetyScore?.breakdown?.map((item) => (
                            <div key={item.key} className="flex items-center justify-between rounded-xl border border-slate-200/50 p-3 dark:border-slate-700/40">
                              <div>
                                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{item.label}</p>
                                <p className="text-xs text-slate-500 dark:text-slate-300">{item.explanation}</p>
                              </div>
                              <div className="text-right text-sm text-slate-600 dark:text-slate-200">
                                {(item.normalizedScore ?? 0).toFixed(0)} / 100
                              </div>
                            </div>
                          ))}
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    {overview?.safetyScore?.deltaPercent !== null && overview?.safetyScore?.deltaPercent !== undefined ? (
                      <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200">
                        {overview?.safetyScore?.deltaPercent >= 0 ? '+' : ''}
                        {overview?.safetyScore?.deltaPercent.toFixed(1)}%
                      </span>
                    ) : null}
                    <span className="text-xs text-slate-500 dark:text-slate-300" title={overview?.comparisonLabel ?? ''}>
                      vs previous period
                    </span>
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200/50 bg-white/80 p-4 dark:border-slate-700/40 dark:bg-slate-900/80">
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Top drivers</p>
                  <p className="text-xs text-slate-500 dark:text-slate-300">{overview?.safetyScore?.topDrivers?.improving}</p>
                  <p className="text-xs text-rose-500">{overview?.safetyScore?.topDrivers?.hurting}</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Violations by severity</p>
                  <div className="mt-2 grid gap-2">
                    {(severityBreakdown?.rows ?? []).length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-slate-200/60 bg-white/70 px-4 py-3 text-xs text-slate-500 dark:border-slate-700/50 dark:bg-slate-900/60 dark:text-slate-300">
                        No severity data yet. This will populate once flagged prompts are reviewed.
                      </div>
                    ) : (
                      (severityBreakdown?.rows ?? []).map((row) => (
                        <div key={row.label} className="flex items-center justify-between rounded-2xl border border-slate-200/60 bg-white/80 px-3 py-2 text-xs dark:border-slate-700/50 dark:bg-slate-900/70">
                          <span className="text-slate-600 dark:text-slate-300">{row.label}</span>
                          <span className="font-semibold text-slate-900 dark:text-slate-100">{row.c}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Top blocked topics</p>
                  <div className="mt-2 grid gap-2">
                    {(topicBreakdown?.rows ?? []).length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-slate-200/60 bg-white/70 px-4 py-3 text-xs text-slate-500 dark:border-slate-700/50 dark:bg-slate-900/60 dark:text-slate-300">
                        No blocked topics yet. This appears after any topics are flagged.
                      </div>
                    ) : (
                      (topicBreakdown?.rows ?? []).slice(0, 4).map((row) => (
                        <div key={row.label} className="flex items-center justify-between rounded-2xl border border-slate-200/60 bg-white/80 px-3 py-2 text-xs dark:border-slate-700/50 dark:bg-slate-900/70">
                          <span className="text-slate-600 dark:text-slate-300">{row.label}</span>
                          <span className="font-semibold text-slate-900 dark:text-slate-100">{row.c}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="rounded-3xl border border-slate-200/40 bg-white/80 shadow-[0_24px_60px_-45px_rgba(30,64,175,0.35)] dark:border-slate-700/40 dark:bg-slate-900/80">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-rose-500" />
                <CardTitle className="text-lg text-slate-900 dark:text-slate-100">Needs Attention</CardTitle>
              </div>
              <Button
                variant="ghost"
                className="rounded-full text-slate-500 dark:text-slate-200 dark:hover:text-white dark:hover:bg-slate-800/80"
                onClick={() => navigate('/flags')}
              >
                View all
              </Button>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {(overview?.needsAttention ?? []).length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-300">No immediate risks detected.</p>
              ) : (
                overview?.needsAttention?.map((item) => (
                  <div
                    key={item.key}
                    className="flex items-center justify-between rounded-2xl bg-slate-50/80 px-3 py-2 dark:bg-slate-800/80"
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{item.label}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-300">{item.description}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="rounded-full text-xs"
                      onClick={() => navigate(item.action.href)}
                    >
                      {item.action.label}
                      <ArrowRight className="ml-1 h-3 w-3" />
                    </Button>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Card className="rounded-3xl border border-slate-200/40 bg-white/80 shadow-[0_24px_60px_-45px_rgba(30,64,175,0.35)] dark:border-slate-700/40 dark:bg-slate-900/80">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-500" />
                <CardTitle className="text-lg text-slate-900 dark:text-slate-100">Investor Progress</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-400 dark:text-slate-300">Feature adoption</p>
                {(overview?.progress?.featureAdoption ?? []).map((item) => (
                  <div key={item.label} className="flex items-center justify-between rounded-full bg-slate-50/80 px-3 py-2 dark:bg-slate-800/80">
                    <span className="text-slate-600 dark:text-slate-300">{item.label}</span>
                    <span className="font-semibold text-slate-900 dark:text-slate-100">
                      {item.value}/{item.total || 0}
                    </span>
                  </div>
                ))}
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-400 dark:text-slate-300">System health</p>
                {(overview?.progress?.systemHealth ?? []).map((item) => (
                  <div key={item.label} className="flex items-center justify-between rounded-full bg-slate-50/80 px-3 py-2 dark:bg-slate-800/80">
                    <span className="text-slate-600 dark:text-slate-300">{item.label}</span>
                    <span className="font-semibold text-slate-900 dark:text-slate-100">{item.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      {(loadingOverview || loadingTrends) && (
        <Card className="rounded-3xl border border-slate-200/40 bg-white/80 p-6 text-sm text-slate-500 dark:border-slate-700/40 dark:bg-slate-900/80 dark:text-slate-300">
          Loading dashboard data…
        </Card>
      )}
      {overviewError && (
        <Card className="rounded-3xl border border-rose-200/40 bg-white/80 p-6 text-sm text-rose-600 dark:border-rose-600/40 dark:bg-slate-900/80">
          Failed to load dashboard data. Please retry.
        </Card>
      )}
    </div>
  );
}

function MultiLineChart({ series, isLoading }: { series: Series[]; isLoading?: boolean }) {
  if (isLoading) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700/40 p-6 text-sm text-slate-500 dark:text-slate-300">
        Loading trends…
      </div>
    );
  }
  if (!series || series.length === 0 || series.every((s) => s.points.length === 0)) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700/40 p-6 text-sm text-slate-500 dark:text-slate-300">
        No usage data yet.
      </div>
    );
  }

  const allPoints = series.flatMap((s) => s.points);
  const max = Math.max(...allPoints.map((p) => p.c), 1);
  const min = Math.min(...allPoints.map((p) => p.c), 0);
  const norm = (v: number) => ((v - min) / (max - min || 1)) * 100;

  return (
    <div className="rounded-2xl border border-slate-200/60 bg-gradient-to-b from-white to-[#eef3ff] p-4 dark:border-slate-700/60 dark:from-slate-900/70 dark:to-slate-900/40">
      <svg viewBox="0 0 100 100" className="h-32 w-full" preserveAspectRatio="none">
        {series.map((serie, idx) => {
          const path = serie.points
            .map((p, index) => {
              const x = (index / Math.max(serie.points.length - 1, 1)) * 100;
              const y = 100 - norm(p.c);
              return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
            })
            .join(' ');
          const color = idx === 0 ? '#4C6EF5' : idx === 1 ? '#34D399' : '#F97316';
          return (
            <path
              key={serie.key}
              d={path}
              fill="none"
              stroke={color}
              strokeWidth="2"
              strokeLinecap="round"
            />
          );
        })}
      </svg>
      <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-500 dark:text-slate-300">
        {series.map((serie, idx) => {
          const color = idx === 0 ? 'bg-blue-500' : idx === 1 ? 'bg-emerald-400' : 'bg-orange-400';
          return (
            <span key={serie.key} className="flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full ${color}`} />
              {serie.label}
            </span>
          );
        })}
      </div>
    </div>
  );
}
